import { useEffect, useState } from 'react';
import {
  getAuth,
  onAuthStateChanged,
  getRedirectResult,
  GoogleAuthProvider,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import getUserSession from 'features/auth/api/getUserSession';
import serverErrorHandler from 'services/serverError.service';
import { setPendingRedirectCredential } from 'utils/redirectCredentialStore';
import useUserStore from 'store/useUserStore';

/**
 * This hook will fetch user profile data for the logged in user.
 * The data will only be fetched if there is user session token
 * but no user session profile data
 */
export default function useAuth() {
  const [isDone, setIsDone] = useState(false);

  useEffect(() => {
    const auth = getAuth();

    // Process any pending redirect result at app initialization (mobile/Safari sign-in flow).
    // Called here rather than inside the lazily-loaded Login component so the redirect result
    // is consumed immediately on page load, before any routing decisions are made.
    getRedirectResult(auth)
      .then(() => {
        // Success: onAuthStateChanged below will fire with the authenticated user.
      })
      .catch((e: unknown) => {
        if (!(e instanceof FirebaseError)) {
          serverErrorHandler(e);
          return;
        }
        if (e.code === 'auth/account-exists-with-different-credential') {
          const credential = GoogleAuthProvider.credentialFromError(e);
          const email = (e.customData?.email as string) ?? null;
          setPendingRedirectCredential(credential, email);
        } else if (
          e.code === 'auth/web-storage-unsupported' ||
          e.code === 'auth/operation-not-supported-in-this-environment'
        ) {
          // Safari ITP blocks Firebase's cross-origin storage after a redirect.
          // These errors are expected and non-actionable — suppress the toaster.
        } else {
          serverErrorHandler(e);
        }
      });

    /**
     * Makes request for user data. Data is stored
     * in session store
     */
    async function fetchUserAsync() {
      try {
        const response = await getUserSession();

        useUserStore.getState().setUser(response);
      } catch (e) {
        serverErrorHandler(e);
      }
      setIsDone(true);
    }

    // listen for firebase user state change
    onAuthStateChanged(auth, async user => {
      if (user) {
        // since we have user, fetch user data
        fetchUserAsync();
      } else {
        // user is not logged, clear session store
        useUserStore.getState().logout();
        setIsDone(true);
      }
    });
  }, []);

  return { isDone };
}
